import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import './ImageCard.css';

export interface ImageCardProps {
  id: string;
  image: string;
  title: string;
  subtitle?: string;
  description?: string;
  status?: 'active' | 'pending' | 'suspended' | 'deactivated';
  onActionClick?: (id: string, event: React.MouseEvent) => void;
  onCardClick?: (id: string) => void;
}

const ImageCard: React.FC<ImageCardProps> = ({
  id,
  image,
  title,
  subtitle,
  description,
  status = 'active',
  onActionClick,
  onCardClick,
}) => {
  const getStatusConfig = (status: string) => {
    const statusConfig: Record<string, { label: string; color: string }> = {
      active: { label: '', color: '' },
      pending: { label: 'Pending review', color: 'var(--palette-light-status-warning-value-hex)' },
      suspended: { label: 'Suspended', color: 'var(--palette-light-status-danger-value-hex)' },
      deactivated: { label: 'Deactivated', color: 'var(--palette-light-background-tertiary-value-hex)' },
    };

    const config = statusConfig[status];
    if (!config || !config.label) return null;

    return (
      <Box
        className="image-card-status-badge"
        sx={{
          position: 'absolute',
          top: '8px',
          left: '8px',
          backgroundColor: config.color,
          color: status === 'deactivated' ? 'var(--palette-light-text-primary-value-hex)' : 'white',
          fontSize: '14px',
          fontWeight: 400,
          lineHeight: '1.4',
          padding: '0px 12px',
          borderRadius: '8px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          zIndex: 2,
        }}
      >
        {config.label}
      </Box>
    );
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onActionClick) {
      onActionClick(id, e);
    }
  };

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(id);
    }
  };

  return (
    <Box
      className="image-card"
      onClick={handleCardClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        backgroundColor: 'white',
        cursor: onCardClick ? 'pointer' : 'default',
      }}
    >
      {/* Image Area */}
      <Box sx={{ position: 'relative', width: '100%', height: '249.25px' }}>
        <Box
          component="img"
          src={image}
          alt={title}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '16px',
            opacity: status === 'active' ? 1 : 0.5,
          }}
        />
        {getStatusConfig(status)}
        {onActionClick && (
          <IconButton
            className="image-card-action-button"
            onClick={handleActionClick}
            sx={{
              position: 'absolute',
              bottom: '8px',
              right: '8px',
              width: '32px',
              height: '32px',
              backgroundColor: 'white',
              borderRadius: '16px',
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'var(--palette-light-background-secondary-value-hex)',
              },
            }}
          >
            <MoreHorizIcon sx={{ fontSize: '16px', color: 'var(--palette-light-brand-primary-value-hex)' }} />
          </IconButton>
        )}
      </Box>

      {/* Title */}
      <Typography
        className="image-card-title"
        sx={{
          fontSize: '14px',
          fontWeight: 700,
          lineHeight: '1.4',
          color: 'var(--palette-light-text-primary-value-hex)',
        }}
      >
        {title}
      </Typography>

      {/* Subtitle (e.g., Price) */}
      {subtitle && (
        <Typography
          className="image-card-subtitle"
          sx={{
            fontSize: '14px',
            fontWeight: 700,
            lineHeight: '1.4',
            color: 'var(--palette-light-brand-primary-value-hex)',
          }}
        >
          {subtitle}
        </Typography>
      )}

      {/* Description */}
      {description && (
        <Typography
          className="image-card-description"
          sx={{
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '1.4',
            color: 'var(--palette-light-text-secondary-value-hex)',
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

export default ImageCard;

